import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import type { GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { LojaDataGrid } from "layouts/pages/loja/common";

import CardListCat from "./componets/CardListCat";
import TotalCamisetas from "./componets/TotalCamisetas";
import {
  INSCRITOS_ACCENT,
  inscritosFilterChipSx,
  inscritosSectionSx,
  inscritosToolbarSx,
  inscritosFieldSx,
} from "./lista-inscritos.theme";

import { formatDate, formatDateEndOfDay } from "helpers/formatDate";
import { exportToExcel } from "helpers/exportFileToExcel";
import { cpfApplyMask, numbersOnly, phoneApplyMask } from "helpers/utils";

import { IAtletaInscritoEvento } from "interfaces/atletasInscritosEvento";
import { IEventoResponse } from "interfaces/eventos";

import { getAllAtletaEvento } from "services/reports";

interface Props {
  evento: IEventoResponse;
}

type FiltroStatus = "TODOS" | "PENDENTES" | "CANCELADO" | "INSC_ATIVAS";

export type InscritoRow = {
  id: string;
  nInsc: string;
  nome: string;
  cpf: string;
  status: string;
  dtNasc: string;
  telefone: string;
  email: string;
  sexo: string;
  equipe: string;
  camiseta: string;
  cidade: string;
  distancia: string;
  categoria: string;
  dtInsc: string;
};

const EXCEL_HEADER = [
  "N. Inscricão",
  "Nome Atleta",
  "CPF",
  "Status",
  "DtNasc",
  "telefone",
  "E-mail",
  "Sexo",
  "Equipe",
  "Camisa",
  "Cidade/UF",
  "Distancia",
  "Categoria",
  "Data inscrição",
];

function statusChipColor(
  status: string,
): "success" | "warning" | "error" | "default" {
  const s = status.toLowerCase();
  if (s === "pago") return "success";
  if (s === "pendente") return "warning";
  if (s === "cancelado") return "error";
  return "default";
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? INSCRITOS_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function mapAtletasToRows(data: IAtletaInscritoEvento): InscritoRow[] {
  return data.atletasInscritos.map((listaInscritos) => {
    const cpfRaw = numbersOnly(listaInscritos.atleta.cpf);
    const nInsc = String(listaInscritos.inscricao.numeroInscricao);

    return {
      // Inscrições em grupo compartilham o mesmo número — id precisa ser único por atleta.
      id: `${nInsc}-${cpfRaw}`,
      nInsc,
      nome: listaInscritos.atleta.nome,
      cpf: cpfApplyMask(cpfRaw),
      status: listaInscritos.inscricao.statusInscricao.tipo,
      dtNasc: formatDateEndOfDay(listaInscritos.atleta.dataNascimento),
      telefone: phoneApplyMask(numbersOnly(listaInscritos.atleta.celPhone)),
      email: listaInscritos.atleta.email,
      sexo: listaInscritos.atleta.sexo.tipo.charAt(0).toUpperCase(),
      equipe: listaInscritos.atleta.equipe || "—",
      camiseta: listaInscritos.tamanhoCamiseta.toUpperCase(),
      cidade: `${listaInscritos.atleta.enderecos[0].cidade}/${listaInscritos.atleta.enderecos[0].uf}`,
      distancia: listaInscritos.categoria.distancia.descricao,
      categoria: listaInscritos.categoria.descricao,
      dtInsc: formatDate(
        listaInscritos.inscricao.dataInscricao,
        "dd/MM/yyyy HH:mm",
      ),
    };
  });
}

function rowToExcelObject(row: InscritoRow): Record<string, string> {
  return {
    nInsc: row.nInsc,
    nome: row.nome.toUpperCase(),
    cpf: row.cpf,
    status: row.status,
    dtNasc: row.dtNasc,
    telefone: row.telefone,
    email: row.email,
    sexo: row.sexo,
    equipe: row.equipe,
    camiseta: row.camiseta,
    cidade: row.cidade.toUpperCase(),
    distancia: row.distancia,
    categoria: row.categoria,
    dtInsc: row.dtInsc,
  };
}

export default function ListaInscritos({ evento }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [filtro, setFiltro] = useState<FiltroStatus>("TODOS");
  const [busca, setBusca] = useState("");
  const [rows, setRows] = useState<InscritoRow[]>([]);

  const {
    _count: { inscritos, inscritos_cancelado },
  } = evento;

  const totalPendente = useMemo(
    () => rows.filter((row) => row.status === "Pendente").length,
    [rows],
  );
  const totalInscAtivas = inscritos - inscritos_cancelado;
  const limiteAtingido = totalInscAtivas >= evento.limiteInscricao;

  useEffect(() => {
    let ativo = true;
    setIsLoading(true);
    getAllAtletaEvento(evento.id)
      .then(({ data, status }) => {
        if (!ativo || status !== 200) return;
        setRows(mapAtletasToRows(data));
      })
      .finally(() => {
        if (ativo) setIsLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [evento.id]);

  const rowsPorStatus = useMemo(() => {
    switch (filtro) {
      case "PENDENTES":
        return rows.filter((item) => item.status === "Pendente");
      case "CANCELADO":
        return rows.filter((item) => item.status === "Cancelado");
      case "INSC_ATIVAS":
        return rows.filter((item) => item.status !== "Cancelado");
      case "TODOS":
      default:
        return rows;
    }
  }, [rows, filtro]);

  const rowsFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return rowsPorStatus;

    return rowsPorStatus.filter((row) =>
      [
        row.nInsc,
        row.nome,
        row.cpf,
        row.email,
        row.telefone,
        row.equipe,
        row.cidade,
        row.categoria,
        row.distancia,
        row.status,
      ]
        .join(" ")
        .toLowerCase()
        .includes(termo),
    );
  }, [rowsPorStatus, busca]);

  const camisetaCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of rowsFiltradas) {
      counts[row.camiseta] = (counts[row.camiseta] ?? 0) + 1;
    }
    return counts;
  }, [rowsFiltradas]);

  const camisetaExportOptions = useMemo(
    () =>
      evento.camisetas.map((camiseta) => ({
        description: camiseta.tamanho,
        value: camisetaCounts[camiseta.tamanho.toUpperCase()] ?? 0,
      })),
    [evento.camisetas, camisetaCounts],
  );

  const filtros: Array<{ key: FiltroStatus; label: string; count: number }> = [
    { key: "TODOS", label: "Inscritos", count: inscritos },
    { key: "PENDENTES", label: "Pendentes", count: totalPendente },
    { key: "CANCELADO", label: "Cancelados", count: inscritos_cancelado },
    { key: "INSC_ATIVAS", label: "Insc. ativas", count: totalInscAtivas },
  ];

  const columns: GridColDef<InscritoRow>[] = [
    { field: "nInsc", headerName: "Nº inscrição", width: 120 },
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {params.row.nome}
        </Typography>
      ),
    },
    { field: "cpf", headerName: "CPF", width: 130 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.status}
          color={statusChipColor(params.row.status)}
          variant={
            params.row.status.toLowerCase() === "pago" ? "filled" : "outlined"
          }
        />
      ),
    },
    { field: "dtNasc", headerName: "Nascimento", width: 110 },
    { field: "telefone", headerName: "Telefone", width: 130 },
    { field: "email", headerName: "E-mail", flex: 1, minWidth: 180 },
    { field: "sexo", headerName: "Sexo", width: 70 },
    { field: "equipe", headerName: "Equipe", width: 120 },
    { field: "camiseta", headerName: "Camisa", width: 80 },
    { field: "cidade", headerName: "Cidade/UF", width: 130 },
    { field: "distancia", headerName: "Distância", width: 110 },
    { field: "categoria", headerName: "Categoria", width: 120 },
    { field: "dtInsc", headerName: "Inscrição", width: 140 },
  ];

  const handlerExportExcel = () => {
    exportToExcel({
      title: `Lista de inscritos: ${evento.titulo}`,
      headerItem: EXCEL_HEADER,
      rowsTable: rowsFiltradas.map(rowToExcelObject),
      nameWorkbook: "Inscritos",
      filename: `Evento ${evento.titulo}`,
      options: [{ title: "Camisetas", data: camisetaExportOptions }],
    });
  };

  return (
    <MDBox>
      <Box sx={inscritosToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <GroupsOutlinedIcon sx={{ color: INSCRITOS_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Lista de inscritos
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {limiteAtingido ? (
              <Chip label="Limite atingido" color="error" size="small" />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Limite inscrição: {evento.limiteInscricao}
              </Typography>
            )}
            {inscritos > 0 ? (
              <MDButton
                variant="gradient"
                color="dark"
                onClick={handlerExportExcel}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  flexShrink: 0,
                  background: `linear-gradient(195deg, ${INSCRITOS_ACCENT}, #e04a1a)`,
                }}
              >
                Exportar
              </MDButton>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={4}>
          <StatCard label="Total inscritos" value={inscritos} accent />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard label="Inscrições ativas" value={totalInscAtivas} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Pendentes / cancelados"
            value={`${totalPendente} / ${inscritos_cancelado}`}
          />
        </Grid>
      </Grid>

      {evento.distanciaEvento.length > 0 ? (
        <Box sx={{ ...inscritosSectionSx, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Vagas por distância
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {evento.distanciaEvento.map((distCat) => (
              <CardListCat
                key={distCat.id}
                eventoId={evento.id}
                distancia={distCat}
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      <TotalCamisetas tamCamisetas={evento.camisetas} counts={camisetaCounts} />

      <Box sx={{ ...inscritosSectionSx, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          mb={1.5}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Filtros e busca
          </Typography>
          <TextField
            size="small"
            placeholder="Buscar inscrito..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ minWidth: { xs: "100%", md: 280 }, ...inscritosFieldSx }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={1}>
          {filtros.map((item) => (
            <Chip
              key={item.key}
              clickable
              label={`${item.label} · ${item.count}`}
              onClick={() => setFiltro(item.key)}
              variant="outlined"
              sx={inscritosFilterChipSx(filtro === item.key)}
            />
          ))}
        </Stack>
      </Box>

      {isLoading ? (
        <Box sx={{ py: 2 }}>
          <Skeleton variant="rounded" height={320} />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            bgcolor: "grey.50",
          }}
        >
          <GroupsOutlinedIcon sx={{ fontSize: 44, color: "grey.400", mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nenhum atleta inscrito
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quando houver inscrições neste evento, elas aparecerão aqui.
          </Typography>
        </Box>
      ) : (
        <LojaDataGrid
          rows={rowsFiltradas}
          columns={columns}
          getRowId={(row) => String(row.id)}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 25, 50]}
          localeText={{ noRowsLabel: "Nenhum inscrito neste filtro" }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
          }}
        />
      )}
    </MDBox>
  );
}
